import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '../components/ConfirmModal';
import { getErrorMessage } from '../utils/error-helpers';
import { usersApi } from './api';
import type { UserListItem } from './types';
import '../dashboard/superadmin-dashboard.css';

/* ─── Pure helpers ──────────────────────────────────── */

function getRoleBadgeClass(roleName: string): string {
  if (roleName === 'superadmin') return 'role-superadmin';
  if (roleName === 'admin') return 'role-admin';
  if (roleName === 'user_high') return 'role-high';
  if (roleName === 'user_medium') return 'role-medium';
  if (roleName === 'user_low') return 'role-low';
  return 'role-default';
}

function formatDate(date: Date | string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('es-AR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
}

/* ─── Row sub-component ─────────────────────────────── */

interface UserRowProps {
  user: UserListItem;
  onEdit: (id: string) => void;
  onDeleteClick: (id: string, email: string) => void;
}

function UserRow({ user, onEdit, onDeleteClick }: UserRowProps): JSX.Element {
  const { t } = useTranslation('admin');
  const isSuperAdmin = user.role.name === 'superadmin';
  return (
    <tr className="table-row">
      <td className="table-cell-name">
        <span className="user-display-name">{user.fullName}</span>
        <span className="user-display-email">{user.email}</span>
      </td>
      <td className="table-cell-role">
        <span className={`role-badge ${getRoleBadgeClass(user.role.name)}`}>
          {t(`users.roles.${user.role.name}`, { defaultValue: user.role.name })}
        </span>
      </td>
      <td>
        <span className={`status-badge ${user.isActive ? 'status-active' : 'status-inactive'}`}>
          {user.isActive ? t('users.active') : t('users.inactive')}
        </span>
      </td>
      <td className="table-cell-tenant">{user.tenant.name}</td>
      <td className="table-cell-date">{formatDate(user.createdAt)}</td>
      <td className="table-cell-actions">
        {!isSuperAdmin ? (
          <>
            <button type="button" className="btn-table-action btn-edit"
              onClick={() => onEdit(user.id)} title={t('users.editTooltip')}>
              {t('users.edit')}
            </button>
            <button type="button" className="btn-table-action btn-delete"
              onClick={() => onDeleteClick(user.id, user.email)} title={t('users.deleteTooltip')}>
              {t('users.delete')}
            </button>
          </>
        ) : (
          <span className="table-protected">{t('users.protected')}</span>
        )}
      </td>
    </tr>
  );
}

/* ─── Data hook ─────────────────────────────────────── */

function useUsersPage() {
  const { t } = useTranslation('admin');
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<{ id: string; email: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadUsers = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const data = await usersApi.list();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, t('users.errorLoading')));
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void loadUsers(); }, []);

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!userToDelete) return;
    try {
      setIsDeleting(true);
      await usersApi.delete(userToDelete.id);
      setUserToDelete(null);
      await loadUsers();
    } catch (err) {
      setError(getErrorMessage(err, t('users.errorDeleting')));
      setUserToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return { users, isLoading, error, userToDelete, isDeleting,
    handleDeleteClick: (id: string, email: string) => setUserToDelete({ id, email }),
    handleDeleteConfirm,
    closeDeleteModal: () => setUserToDelete(null),
  };
}

/* ─── Main export ────────────────────────────────────── */

export function UsersList(): JSX.Element {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { users, isLoading, error, userToDelete, isDeleting,
    handleDeleteClick, handleDeleteConfirm, closeDeleteModal } = useUsersPage();

  if (isLoading) {
    return (
      <div className="table-loading">
        <div className="loading-skeleton" /><div className="loading-skeleton" /><div className="loading-skeleton" />
      </div>
    );
  }

  return (
    <div className="dashboard-table-container">
      {error && (
        <div className="sa-error-banner" role="alert">
          <span className="sa-error-dot" aria-hidden="true" /><span>{error}</span>
        </div>
      )}
      <div className="dashboard-table-header">
        <div>
          <h2 className="table-title">{t('users.title')}</h2>
          <p className="table-subtitle">{t('users.subtitle')}</p>
        </div>
        <button type="button" className="btn-create" onClick={() => navigate('/admin/users/new')}>
          {t('users.create')}
        </button>
      </div>
      {users.length === 0 ? (
        <div className="table-empty">
          <p>{t('users.empty')}</p>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/users/new')}>
            {t('users.createFirst')}
          </button>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="dashboard-table">
            <thead className="table-head">
              <tr>
                <th>{t('users.table.name')}</th>
                <th>{t('users.table.role')}</th>
                <th>Estado</th>
                <th>Organización</th>
                <th>{t('users.table.createdAt')}</th>
                <th className="table-actions-header">{t('users.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map(user => (
                <UserRow key={user.id} user={user}
                  onEdit={id => navigate(`/admin/users/${id}/edit`)}
                  onDeleteClick={handleDeleteClick} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmModal
        isOpen={userToDelete !== null}
        onClose={closeDeleteModal}
        onConfirm={() => { void handleDeleteConfirm(); }}
        title={t('users.deleteConfirm.title')}
        message={t('users.deleteConfirm.message', { email: userToDelete?.email ?? '' })}
        confirmText={t('users.deleteConfirm.confirmButton')}
        cancelText={t('users.deleteConfirm.cancelButton')}
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}
